import { useState } from 'react'
import { PromptList } from '@/components/PromptList'
import { PromptDetail } from '@/components/PromptDetail'
import { PromptEditForm } from '@/components/PromptDetail'
import { createPrompt } from '@/services/promptsApi'
import type { UpdatePromptData } from '@/types'

export function PromptsPage() {
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null)
  const [isCreateMode, setIsCreateMode] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const handlePromptSelect = (id: string) => {
    setSelectedPromptId(id)
    setIsCreateMode(false)
  }

  const handleBack = () => {
    setSelectedPromptId(null)
    setIsCreateMode(false)
    setSaveError(null)
  }

  const handleCreateNew = () => {
    setSelectedPromptId(null)
    setIsCreateMode(true)
    setSaveError(null)
  }

  const handleCreate = async (data: UpdatePromptData) => {
    setIsSaving(true)
    setSaveError(null)
    try {
      const created = await createPrompt({
        name: data.name!,
        use_case: data.use_case!,
        prompt_text: data.prompt_text!,
        model_id: data.model_id!,
      })
      setSelectedPromptId(created.id)
      setIsCreateMode(false)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to create prompt')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div data-testid="prompts-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-bold text-gray-900">Prompts</h1>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {isCreateMode ? (
          <div className="p-6 space-y-6">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="text-gray-500 hover:text-gray-700"
                data-testid="back-btn"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <h2 className="text-xl font-semibold text-gray-900">Create New Prompt</h2>
            </div>

            {saveError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="error-message">
                <p className="text-red-800">{saveError}</p>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <PromptEditForm
                onSave={handleCreate}
                onCancel={handleBack}
                isSaving={isSaving}
                isCreateMode={true}
              />
            </div>
          </div>
        ) : selectedPromptId ? (
          <PromptDetail promptId={selectedPromptId} onBack={handleBack} />
        ) : (
          <PromptList
            onPromptSelect={handlePromptSelect}
            onCreateNew={handleCreateNew}
          />
        )}
      </main>
    </div>
  )
}
